import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { rankTitle, fmtSystemDate } from "@/lib/format";
import { assetUrl } from "@/lib/branding";

/**
 * Pilot header — pulled from vacanada.org via the backend scraper endpoint.
 * Two-row layout:
 *   Row 1  · UTC clock (top-right, subtle)
 *   Row 2  · avatar + hat + wings + epaulette + name / rank / role
 */
export default function PilotHeader({ user }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const { data: card } = useQuery({
    queryKey: ["profile-card", user?.pid],
    queryFn: () => api.get("/pilot/profile-card").then((r) => r.data),
    enabled: !!user?.pid,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  if (!user) return null;

  const rank = Number(user.rank || 0);
  const captain = rank >= 4;
  // assetUrl, not a leading slash — these are served from public/ and the
  // desktop shell loads the page from file://, where "/insignia/..." points at
  // the root of the drive. Same bug as the logo. The card's own URLs (below)
  // are absolute http(s) from the portal and need no help.
  const localHat   = assetUrl(captain ? "insignia/hat_CAg.gif"   : "insignia/hat_FOg.gif");
  const localWings = assetUrl(captain ? "insignia/wings_CAg.gif" : "insignia/wings_FOg.gif");
  const localEp    = assetUrl(captain ? "insignia/ep_CAg.gif"    : "insignia/ep_FOg.gif");

  const hat   = card?.hat        || localHat;
  const wings = card?.wings      || localWings;
  const ep    = card?.epaulette  || localEp;
  const rankLine = card?.rank_title || rankTitle(user.rank);
  const roleLine = card?.role_line  || `${(user.hub || "").replace(/^C/i, "")} ${user.is_admin ? "Flight Ops" : "Line pilot"}`.trim();
  const name = card?.name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const initials = ((user.first_name?.[0] || "") + (user.last_name?.[0] || "")).toUpperCase() || "VA";

  return (
    <section className="card-flat mb-6 overflow-hidden" data-testid="pilot-header">
      {/* Slim top strip with UTC time */}
      <div className="flex items-center justify-between px-6 py-2 border-b border-border bg-secondary/40">
        <div className="overline">Crew centre · {user.pid}</div>
        <div className="text-xs font-mono tracking-wide text-muted-foreground" data-testid="pilot-header-sysdate">
          {fmtSystemDate(now)}
        </div>
      </div>

      {/* Main row */}
      <div className="px-6 py-5 flex items-center gap-6 flex-wrap">
        {/* Avatar */}
        <div
          className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0"
          data-testid="pilot-avatar"
        >
          {initials}
        </div>

        {/* Name + rank + role — takes the flex */}
        <div className="min-w-0 flex-1">
          <div className="text-2xl font-bold tracking-tight leading-tight" data-testid="pilot-header-name">
            {name}
          </div>
          <div className="text-sm font-semibold mt-0.5" data-testid="pilot-header-rank">
            {rankLine}
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-0.5" data-testid="pilot-header-role">
            {roleLine}
          </div>
        </div>

        {/* Insignia group — right-aligned, sits like a badge */}
        <div
          className="flex items-center gap-5 shrink-0 pl-6 border-l border-border"
          data-testid="pilot-insignia"
        >
          <div className="flex flex-col items-center">
            <img
              src={hat}
              alt="Rank hat"
              data-testid="pilot-hat"
              onError={(e) => { e.currentTarget.src = localHat; }}
              className="h-14 w-auto object-contain"
            />
            <span className="overline-muted mt-1 text-[9px]">Hat</span>
          </div>
          <div className="flex flex-col items-center">
            <img
              src={wings}
              alt="Pilot wings"
              data-testid="pilot-wings"
              onError={(e) => { e.currentTarget.src = localWings; }}
              className="h-10 w-auto object-contain"
            />
            <span className="overline-muted mt-1 text-[9px]">Wings</span>
          </div>
          <div className="flex flex-col items-center">
            <img
              src={ep}
              alt="Epaulette"
              data-testid="pilot-epaulette"
              onError={(e) => { e.currentTarget.src = localEp; }}
              className="h-12 w-auto object-contain"
            />
            <span className="overline-muted mt-1 text-[9px]">Ep.</span>
          </div>
        </div>
      </div>
    </section>
  );
}
